import { useEffect, useRef, ReactNode } from 'react';
import { X } from 'lucide-react';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  height?: 'full' | 'half' | 'auto';
  className?: string;
  zIndex?: number;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  height = 'auto',
  className = '',
  zIndex
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Focus management: save previous focus, move to sheet, restore on close
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      sheetRef.current?.focus();
    } else {
      previousFocusRef.current?.focus();
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle swipe down to close
  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    if (diff > 0 && sheetRef.current) {
      // Prevent scrolling past closed position
      const translateY = Math.min(diff, window.innerHeight);
      sheetRef.current.style.transform = `translateY(${translateY}px)`;
    }
  };

  const handleTouchEnd = () => {
    const diff = currentY.current - startY.current;

    if (sheetRef.current) {
      // If swiped down more than 100px, close the sheet
      if (diff > 100) {
        onClose();
      }
      // Reset transform
      sheetRef.current.style.transform = '';
    }
  };

  if (!isOpen) return null;

  const heightClasses = {
    full: 'h-[95vh]',
    half: 'h-[50vh]',
    auto: 'max-h-[85vh]'
  }[height];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
        style={{ zIndex: zIndex ? zIndex - 5 : 'var(--z-modal-backdrop)' }}
        onClick={onClose}
        aria-hidden="true"
        role="presentation"
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`fixed inset-x-0 ${heightClasses} overflow-hidden
                   rounded-t-3xl shadow-2xl transition-transform duration-300
                   ${className}`}
        style={{
          bottom: zIndex ? 'var(--bottom-nav-height, 64px)' : 0,
          zIndex: zIndex || 'var(--z-modal)',
          background: 'var(--bottom-sheet-bg)',
          boxShadow: 'var(--bottom-sheet-shadow)',
          backdropFilter: 'blur(20px)'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bottom-sheet-title"
        tabIndex={-1}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div
            className="w-12 h-1 rounded-full"
            style={{ background: 'var(--bottom-sheet-handle)' }}
            aria-hidden="true"
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-4 border-b border-neutral-200">
          <h2
            id="bottom-sheet-title"
            className="text-xl font-semibold text-neutral-900"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center
                     hover:bg-neutral-100 transition-colors duration-200
                     focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-neutral-600" />
          </button>
        </div>

        {/* Content */}
        <div
          className="px-6 overflow-y-auto"
          style={{
            height: 'calc(100% - 80px)', // Subtract header + handle height
            paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)'
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
